/**
 * Plan 12, Task 2.1 — Island instanced mesh builder.
 *
 * Clusters board nodes by `island-N` prefix and builds ONE big island
 * geometry per cluster, sized to encompass all its child tiles.
 *
 * GLB path: when chunkLargeGltfScene or chunkSmallGltfScene is supplied,
 * clones the chunk GLB once per cluster and scales it from assets/render-scales.json.
 *
 * Procedural fallback: CylinderGeometry sized per cluster with grass-green
 * color (#6BAA4F).
 *
 * nodeToInstance is always an EMPTY map — per-node instance tracking is
 * no longer applicable. Each island Object3D is named `island-N` for
 * downstream lookup via islandsParent.children.find(c => c.name === 'island-1').
 */

import * as THREE from 'three';
import renderScales from '@shared/render-scales';
import type { BoardNode } from '../../../shared/board/types';
import type { SkyforgeSchema } from '../maps/schema';

export interface IslandMeshResult {
  /**
   * The root Object3D for all island platforms. Add to scene groups.board.
   * A THREE.Group named 'islands' containing one Object3D per island cluster.
   * Each child is named `island-N` (e.g. 'island-1', 'island-2').
   */
  readonly object: THREE.Object3D;
  /**
   * Always an EMPTY map. Per-node instance tracking is not applicable
   * in the clustered island design. Downstream consumers should look up
   * islands by name: islandsParent.children.find(c => c.name === 'island-1').
   */
  readonly nodeToInstance: ReadonlyMap<string, number>;
  /**
   * Per-island computed data: centroid, radius, surfaceY.
   * Used by bridges (edge endpoints) and tile-markers (hex prism Y position).
   */
  readonly islandData: ReadonlyMap<
    string,
    {
      centroid: { x: number; y: number; z: number };
      radius: number;
      bridgeAttachmentRadius: number;
      bridgeAttachmentBounds?: IslandBridgeAttachmentBounds;
      surfaceY: number;
    }
  >;
  /** Dispose geometry + material. */
  dispose(): void;
}

export interface IslandSurfaceData {
  readonly centroid: { x: number; y: number; z: number };
  readonly radius: number;
  readonly bridgeAttachmentRadius: number;
  readonly bridgeAttachmentBounds?: IslandBridgeAttachmentBounds;
  readonly surfaceY: number;
}

export interface IslandBridgeAttachmentBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

export interface IslandModelMetrics {
  readonly bridgeAttachmentBounds: IslandBridgeAttachmentBounds;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cluster helpers
// ─────────────────────────────────────────────────────────────────────────────

function clusterNodesByIsland(nodes: readonly BoardNode[]): Map<string, BoardNode[]> {
  const clusters = new Map<string, BoardNode[]>();
  for (const node of nodes) {
    if (node.type === 'pathing') continue; // pathing nodes are routing-only, not on islands
    const match = /^(island-\d+)-/.exec(node.id);
    if (!match) continue; // skip nodes without island prefix
    const islandId = match[1]!;
    if (!clusters.has(islandId)) clusters.set(islandId, []);
    clusters.get(islandId)!.push(node);
  }
  return clusters;
}

function islandCentroid(islandNodes: readonly BoardNode[]): { x: number; y: number; z: number } {
  let sx = 0,
    sy = 0,
    sz = 0;
  for (const n of islandNodes) {
    sx += n.position.x;
    sy += n.position.y;
    sz += n.position.z;
  }
  const c = islandNodes.length;
  return { x: sx / c, y: sy / c, z: sz / c };
}

function islandCenter(
  islandNodes: readonly BoardNode[],
  overrideCenter?: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  return overrideCenter != null ? { ...overrideCenter } : islandCentroid(islandNodes);
}

function islandRadius(
  islandNodes: readonly BoardNode[],
  centroid: { x: number; y: number; z: number },
  overrideRadius?: number,
): number {
  if (overrideRadius != null && overrideRadius > 0) return overrideRadius;
  let maxDist = 0;
  for (const n of islandNodes) {
    const dx = n.position.x - centroid.x;
    const dz = n.position.z - centroid.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d > maxDist) maxDist = d;
  }
  return Math.max(
    maxDist + renderScales.island.autoRadiusBase + renderScales.island.autoRadiusEdgeMargin,
    renderScales.island.autoRadiusMin,
  );
}

function hasFiniteBounds(bounds: THREE.Box3): boolean {
  return (
    Number.isFinite(bounds.min.x) &&
    Number.isFinite(bounds.min.z) &&
    Number.isFinite(bounds.max.x) &&
    Number.isFinite(bounds.max.z)
  );
}

export function measureIslandModelMetrics(
  chunkTemplate?: THREE.Object3D | null,
): IslandModelMetrics | null {
  if (chunkTemplate == null) return null;

  chunkTemplate.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(chunkTemplate);
  if (!hasFiniteBounds(bounds)) return null;

  return {
    bridgeAttachmentBounds: {
      minX: bounds.min.x,
      maxX: bounds.max.x,
      minZ: bounds.min.z,
      maxZ: bounds.max.z,
    },
  };
}

export function scaleIslandBridgeAttachmentBounds(
  bounds: IslandBridgeAttachmentBounds,
  horizontalScale: number,
): IslandBridgeAttachmentBounds {
  return {
    minX: bounds.minX * horizontalScale,
    maxX: bounds.maxX * horizontalScale,
    minZ: bounds.minZ * horizontalScale,
    maxZ: bounds.maxZ * horizontalScale,
  };
}

export function computeIslandSurfaceData(
  nodes: readonly BoardNode[],
  islandOverrides?: SkyforgeSchema['islandOverrides'],
  surfaceMode: 'chunk' | 'procedural' = 'chunk',
  chunkModelMetrics?: IslandModelMetrics | null,
): ReadonlyMap<string, IslandSurfaceData> {
  const clusters = clusterNodesByIsland(nodes);
  const islandDataMap = new Map<string, IslandSurfaceData>();

  for (const [islandId, islandNodes] of clusters) {
    const override = islandOverrides?.[islandId];
    const centroid = islandCenter(islandNodes, override?.center);
    const overrideRadius = override?.radius;
    const radius = islandRadius(islandNodes, centroid, overrideRadius);
    let bridgeAttachmentBounds: IslandBridgeAttachmentBounds | undefined;
    let bridgeAttachmentRadius: number;
    let surfaceY: number;

    if (surfaceMode === 'chunk') {
      const horizontalScale =
        (radius / renderScales.island.chunkBaseRadius) * renderScales.island.flatTopCompensation;
      if (chunkModelMetrics != null) {
        bridgeAttachmentBounds = scaleIslandBridgeAttachmentBounds(
          chunkModelMetrics.bridgeAttachmentBounds,
          horizontalScale,
        );
      }
      bridgeAttachmentRadius =
        bridgeAttachmentBounds != null
          ? Math.max(
              Math.abs(bridgeAttachmentBounds.minX),
              Math.abs(bridgeAttachmentBounds.maxX),
              Math.abs(bridgeAttachmentBounds.minZ),
              Math.abs(bridgeAttachmentBounds.maxZ),
            )
          : radius * renderScales.island.flatTopCompensation;
      surfaceY =
        centroid.y +
        renderScales.island.chunkYOffset +
        horizontalScale *
          renderScales.island.chunkYScale *
          renderScales.island.chunkHalfYExtent *
          renderScales.island.chunkSurfaceYScaleFactor;
    } else {
      bridgeAttachmentRadius = radius;
      const meshY = centroid.y - renderScales.island.proceduralHeight / 2;
      surfaceY = meshY + renderScales.island.proceduralHeight / 2;
    }

    islandDataMap.set(islandId, {
      centroid,
      radius,
      bridgeAttachmentRadius,
      bridgeAttachmentBounds,
      surfaceY,
    });
  }

  return islandDataMap;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build island geometry clustered by island-N prefix.
 *
 * GLB path (when chunkLargeGltfScene or chunkSmallGltfScene is provided):
 *   - Clones the chunk GLB once per island cluster.
 *   - Scales to match cluster radius from assets/render-scales.json.
 *   - nodeToInstance is always empty.
 *
 * Procedural fallback (when both GLBs are null/undefined):
 *   - CylinderGeometry sized from assets/render-scales.json per cluster.
 *   - MeshStandardMaterial color #6BAA4F (grass green).
 *
 * @param nodes — all board nodes (pathing nodes are EXCLUDED from clusters).
 * @param chunkLargeGltfScene — optional large chunk GLB (preferred).
 * @param chunkSmallGltfScene — optional small chunk GLB (fallback when large absent).
 * @returns IslandMeshResult
 */
export function buildIslandMesh(
  nodes: readonly BoardNode[],
  chunkLargeGltfScene?: THREE.Group | null,
  chunkSmallGltfScene?: THREE.Group | null,
  islandOverrides?: SkyforgeSchema['islandOverrides'],
): IslandMeshResult {
  const clusters = clusterNodesByIsland(nodes);

  const islandsParent = new THREE.Group();
  islandsParent.name = 'islands';

  // ── GLB path ───────────────────────────────────────────────────────────────
  const chunkTemplate = chunkLargeGltfScene ?? chunkSmallGltfScene ?? null;
  const chunkModelMetrics = measureIslandModelMetrics(chunkTemplate);
  const islandDataMap = computeIslandSurfaceData(
    nodes,
    islandOverrides,
    chunkTemplate != null ? 'chunk' : 'procedural',
    chunkModelMetrics,
  );

  if (chunkTemplate != null) {
    for (const [islandId] of clusters) {
      const islandData = islandDataMap.get(islandId);
      if (islandData == null) continue;
      const { centroid, radius } = islandData;

      const group = chunkTemplate.clone(true);
      const horizontalScale =
        (radius / renderScales.island.chunkBaseRadius) * renderScales.island.flatTopCompensation;
      group.scale.set(
        horizontalScale,
        horizontalScale * renderScales.island.chunkYScale,
        horizontalScale,
      );
      group.position.set(centroid.x, centroid.y + renderScales.island.chunkYOffset, centroid.z);
      group.name = islandId;
      islandsParent.add(group);
    }

    let disposed = false;
    function disposeGlb(): void {
      if (disposed) return;
      disposed = true;
      islandsParent.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose?.();
          const m = obj.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else if (m != null) (m as THREE.Material).dispose();
        }
      });
    }

    return {
      object: islandsParent,
      nodeToInstance: new Map<string, number>(),
      islandData: islandDataMap,
      dispose: disposeGlb,
    };
  }

  // ── Procedural fallback ────────────────────────────────────────────────────
  // One CylinderGeometry per cluster, sized to encompass all tiles.
  const PROCEDURAL_HALF_HEIGHT = renderScales.island.proceduralHeight / 2;
  const disposables: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material }> = [];

  for (const [islandId] of clusters) {
    const islandData = islandDataMap.get(islandId);
    if (islandData == null) continue;
    const { centroid, radius } = islandData;

    const geometry = new THREE.CylinderGeometry(
      radius,
      radius * renderScales.island.proceduralTopRadiusRatio,
      renderScales.island.proceduralHeight,
      24,
    );
    const material = new THREE.MeshStandardMaterial({ color: 0x6baa4f, flatShading: true });
    const mesh = new THREE.Mesh(geometry, material);
    const meshY = centroid.y - PROCEDURAL_HALF_HEIGHT;
    mesh.position.set(centroid.x, meshY, centroid.z);
    mesh.name = islandId;
    islandsParent.add(mesh);
    disposables.push({ geometry, material });
  }

  let disposed = false;
  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const { geometry, material } of disposables) {
      geometry.dispose();
      material.dispose();
    }
  }

  return {
    object: islandsParent,
    nodeToInstance: new Map<string, number>(),
    islandData: islandDataMap,
    dispose,
  };
}
